import { z } from 'zod';

// Browser-safe contracts of the Onboarding module (zod only). Onboarding ("mise en service") gathers what a new client
// already has, step by step: its founding documents and rules, its lots and members, its finances, its suppliers.
// Files are read by agents into a proposal; deterministic checks say what is complete and what is missing; nothing is
// written to the registry, the books or the documents until a director applies the step.

/** The phases, in order. Identification and the board are filled in forms; the others take files. */
export const ONBOARDING_STEPS = ['identity', 'documents', 'members', 'finances', 'vendors', 'board'] as const;
export const OnboardingStep = z.enum(ONBOARDING_STEPS);
export type OnboardingStep = z.infer<typeof OnboardingStep>;
export const FILE_STEPS: readonly OnboardingStep[] = ['documents', 'members', 'finances', 'vendors'];
/** Phases that may be skipped (and taken up later). */
export const OPTIONAL_STEPS: readonly OnboardingStep[] = ['documents', 'finances', 'vendors'];
/** Phases that only exist while a client is being onboarded from the console. */
export const CONSOLE_STEPS: readonly OnboardingStep[] = ['identity', 'board'];
export type StepStatus = 'empty' | 'reading' | 'ready' | 'applied' | 'skipped';

/** Formats the readers understand. Images (photos, scans without text) are kept but not read. */
export const ONBOARDING_FILE_TYPES = [
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;
export const ONBOARDING_MAX_BYTES = 20 * 1024 * 1024;
export const ONBOARDING_MAX_FILES = 20;

export interface OnboardingFile {
  key: string;
  name: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
  /** Already applied (documents filed, members created…): not applied twice. */
  applied: boolean;
}

const cents = z.number().int().min(-1_000_000_000_00).max(1_000_000_000_00);
const date = z.iso.date();
const text = (max: number) => z.string().trim().max(max);

// --- Proposals (what the readers found), one shape per step

export const DocumentCategory = z.enum(['declaration', 'letters_patent', 'bylaws', 'servitudes', 'minutes', 'insurance', 'contingency_study', 'logbook', 'financial_statements', 'notice', 'other']);
export const DocumentsProposal = z.object({
  documents: z.array(
    z.object({
      fileKey: z.string(),
      fileName: text(200),
      readable: z.boolean(),
      category: DocumentCategory,
      title: text(200),
      documentDate: date.nullable(),
      visibility: z.enum(['board', 'members']),
    }),
  ),
  rules: z
    .object({
      voteBasis: z.enum(['quote_parts', 'lots', 'persons']).nullable(),
      quorumPercent: z.number().min(1).max(100).nullable(),
      noticeMinDays: z.number().int().min(0).max(90).nullable(),
      noticeMaxDays: z.number().int().min(1).max(180).nullable(),
      boardQuorumPercent: z.number().min(1).max(100).nullable(),
      /** Where the rules were read (document and article), for the board to check. */
      source: text(300),
    })
    .nullable(),
  fiscalYearStartMonth: z.number().int().min(1).max(12).nullable(),
});
export type DocumentsProposal = z.infer<typeof DocumentsProposal>;

export const UnitKind = z.enum(['residential', 'commercial', 'parking', 'storage', 'other']);
export const MemberOwner = z.object({
  name: text(200).min(1),
  email: z.email().nullable(),
  phone: text(30).nullable(),
  mailingAddress: text(300).nullable(),
  language: z.enum(['fr', 'en']).nullable(),
});
export const MemberRow = z.object({
  lot: text(50).min(1),
  cadastralLot: text(50).nullable(),
  kind: UnitKind,
  /** Relative value (quote-part) as printed, in percent; null when the list has none (associations: equal shares). */
  sharePercent: z.number().positive().max(100).nullable(),
  owners: z.array(MemberOwner).max(10),
});
export type MemberRow = z.infer<typeof MemberRow>;
export const MembersProposal = z.object({
  building: z.object({ name: text(200).nullable(), streetAddress: text(300).nullable(), city: text(100).nullable(), postalCode: text(10).nullable() }),
  rows: z.array(MemberRow).max(2000),
  source: z.enum(['spreadsheet', 'document']),
  /** A document listed more lots than could be read: send a spreadsheet instead. */
  truncated: z.boolean(),
});
export type MembersProposal = z.infer<typeof MembersProposal>;

export const FinancesProposal = z.object({
  fiscalYear: z.object({ label: text(50).min(1), start: date, end: date }).nullable(),
  /** Date of the balances (usually the last year end). */
  balancesDate: date.nullable(),
  bank: z.object({ operating: cents.nullable(), reserve: cents.nullable(), investments: cents.nullable() }),
  /** Amounts owed by lots at that date. */
  arrears: z.array(z.object({ lot: text(50).min(1), amountCents: cents })).max(2000),
  /** Annual budget lines mapped to the chart of accounts. */
  budget: z.array(z.object({ label: text(200), accountNumber: z.string().regex(/^\d{4}$/), fundCode: z.enum(['OP', 'CF']), amountCents: cents })).max(200),
});
export type FinancesProposal = z.infer<typeof FinancesProposal>;

export const VendorTrade = z.enum(['general', 'plumbing', 'electrical', 'hvac', 'roofing', 'elevator', 'fire_safety', 'envelope', 'landscaping', 'snow_removal', 'cleaning', 'pest_control', 'locksmith', 'painting', 'professional', 'other']);
export const VendorsProposal = z.object({
  vendors: z
    .array(
      z.object({
        name: text(200).min(1),
        trade: VendorTrade,
        contactName: text(200).nullable(),
        email: z.email().nullable(),
        phone: text(50).nullable(),
        rbqLicence: text(30).nullable(),
        service: text(300).nullable(),
      }),
    )
    .max(500),
});
export type VendorsProposal = z.infer<typeof VendorsProposal>;

/** Identification: who the client is, its main address (the building is created from it), the contact person. */
export const IdentityProposal = z.object({
  streetAddress: text(300).min(1),
  city: text(100).min(1),
  postalCode: text(10).nullable(),
  contactName: text(200).nullable(),
  contactEmail: z.email().nullable(),
  contactPhone: text(30).nullable(),
  lotsEstimate: z.number().int().min(1).max(5000).nullable(),
  plan: z.enum(['autonome', 'assistee', 'complete']).nullable(),
  notes: text(2000).nullable(),
});
export type IdentityProposal = z.infer<typeof IdentityProposal>;

/** Board and activation: the first directors, chosen among the imported people. */
export const BoardProposal = z.object({ directors: z.array(z.object({ partyId: z.uuid() })).max(15) });
export type BoardProposal = z.infer<typeof BoardProposal>;

export const PROPOSALS = { identity: IdentityProposal, documents: DocumentsProposal, members: MembersProposal, finances: FinancesProposal, vendors: VendorsProposal, board: BoardProposal } as const;
export type Proposal = IdentityProposal | DocumentsProposal | MembersProposal | FinancesProposal | VendorsProposal | BoardProposal;

export interface OnboardingStepState {
  step: OnboardingStep;
  status: StepStatus;
  files: OnboardingFile[];
  proposal: Proposal | null;
  /** The reader failed (the files stay; upload others or retry). */
  error: string | null;
  /** What applying did, in a sentence. */
  appliedSummary: string | null;
  appliedAt: string | null;
  updatedAt: string | null;
}

// --- Checks: deterministic, shown as "good / warning / missing"

export type CheckLevel = 'ok' | 'warning' | 'missing';
export interface Check {
  level: CheckLevel;
  code: string;
  count?: number;
  /** A few examples (lots, names, files) to help the board complete. */
  items?: string[];
}

export interface CheckContext {
  structure: 'divided_coownership' | 'npo_with_servitudes';
  /** Lots known (registry, or the members proposal), to check arrears against. */
  lots: string[];
  /** The main address is known (a building exists): the members step does not need it. */
  hasBuilding: boolean;
  /** People that can be named directors: id → has an email. */
  people: Record<string, { name: string; email: boolean }>;
}

const examples = (list: string[]) => list.slice(0, 6);

export function checksFor(step: OnboardingStep, proposal: Proposal | null, ctx: CheckContext): Check[] {
  if (!proposal) return [];
  if (step === 'identity') {
    const p = proposal as IdentityProposal;
    return [
      { level: 'ok', code: 'address_given' },
      p.contactEmail ? { level: 'ok', code: 'contact_given' } : { level: 'warning', code: 'contact_missing' },
      p.plan ? { level: 'ok', code: 'plan_chosen' } : { level: 'warning', code: 'plan_missing' },
    ];
  }
  if (step === 'board') {
    const p = proposal as BoardProposal;
    if (!p.directors.length) return [{ level: 'missing', code: 'directors_missing' }];
    const noEmail = p.directors.filter((d) => !ctx.people[d.partyId]?.email).map((d) => ctx.people[d.partyId]?.name ?? '?');
    return [{ level: 'ok', code: 'directors', count: p.directors.length }, ...(noEmail.length ? [{ level: 'missing' as const, code: 'directors_no_email', count: noEmail.length, items: examples(noEmail) }] : [])];
  }
  if (step === 'documents') {
    const p = proposal as DocumentsProposal;
    const has = (...c: string[]) => p.documents.some((d) => c.includes(d.category));
    const checks: Check[] = [];
    const founding = ctx.structure === 'npo_with_servitudes' ? ['letters_patent'] : ['declaration'];
    checks.push(has(...founding) ? { level: 'ok', code: 'founding_found' } : { level: 'missing', code: 'founding_missing' });
    checks.push(has('bylaws') ? { level: 'ok', code: 'bylaws_found' } : { level: 'missing', code: 'bylaws_missing' });
    if (ctx.structure === 'npo_with_servitudes') checks.push(has('servitudes') ? { level: 'ok', code: 'servitudes_found' } : { level: 'warning', code: 'servitudes_missing' });
    const minutes = p.documents.filter((d) => d.category === 'minutes').length;
    checks.push(minutes > 0 ? { level: 'ok', code: 'minutes_found', count: minutes } : { level: 'warning', code: 'minutes_missing' });
    if (has('insurance')) checks.push({ level: 'ok', code: 'insurance_found' });
    if (p.rules && p.rules.quorumPercent !== null && p.rules.noticeMinDays !== null) checks.push({ level: 'ok', code: 'rules_found' });
    else checks.push({ level: 'warning', code: p.rules ? 'rules_partial' : 'rules_missing' });
    const unreadable = p.documents.filter((d) => !d.readable).map((d) => d.fileName);
    if (unreadable.length) checks.push({ level: 'warning', code: 'unreadable', count: unreadable.length, items: examples(unreadable) });
    return checks;
  }
  if (step === 'members') {
    const p = proposal as MembersProposal;
    const checks: Check[] = [];
    const owners = p.rows.flatMap((r) => r.owners);
    checks.push(p.rows.length ? { level: 'ok', code: 'lots', count: p.rows.length } : { level: 'missing', code: 'lots_missing' });
    if (owners.length) checks.push({ level: 'ok', code: 'owners', count: new Set(owners.map((o) => o.name.toLowerCase())).size });
    const noOwner = p.rows.filter((r) => r.owners.length === 0).map((r) => r.lot);
    if (noOwner.length) checks.push({ level: 'missing', code: 'lots_without_owner', count: noOwner.length, items: examples(noOwner) });
    const noEmail = owners.filter((o) => !o.email).map((o) => o.name);
    checks.push(noEmail.length ? { level: 'warning', code: 'no_email', count: noEmail.length, items: examples(noEmail) } : { level: 'ok', code: 'all_emails' });
    const noPhone = owners.filter((o) => !o.phone).map((o) => o.name);
    checks.push(noPhone.length ? { level: 'warning', code: 'no_phone', count: noPhone.length, items: examples(noPhone) } : { level: 'ok', code: 'all_phones' });
    const seen = new Map<string, number>();
    for (const r of p.rows) seen.set(r.lot.toLowerCase(), (seen.get(r.lot.toLowerCase()) ?? 0) + 1);
    const dup = [...seen].filter(([, n]) => n > 1).map(([l]) => l);
    if (dup.length) checks.push({ level: 'warning', code: 'duplicate_lots', count: dup.length, items: examples(dup) });
    const withShare = p.rows.filter((r) => r.sharePercent !== null);
    if (withShare.length === p.rows.length && p.rows.length) {
      const total = withShare.reduce((s, r) => s + (r.sharePercent ?? 0), 0);
      checks.push(Math.abs(total - 100) <= 0.5 ? { level: 'ok', code: 'shares_ok' } : { level: 'warning', code: 'shares_sum', count: Math.round(total * 100) / 100 });
    } else if (withShare.length > 0) checks.push({ level: 'warning', code: 'shares_partial', count: p.rows.length - withShare.length });
    else checks.push(ctx.structure === 'npo_with_servitudes' ? { level: 'ok', code: 'equal_shares' } : { level: 'warning', code: 'shares_missing' });
    if (!ctx.hasBuilding && (!p.building.streetAddress || !p.building.city)) checks.push({ level: 'missing', code: 'address_missing' });
    if (p.truncated) checks.push({ level: 'warning', code: 'truncated' });
    return checks;
  }
  if (step === 'finances') {
    const p = proposal as FinancesProposal;
    const checks: Check[] = [];
    checks.push(p.fiscalYear ? { level: 'ok', code: 'fiscal_year' } : { level: 'missing', code: 'fiscal_year_missing' });
    checks.push(p.bank.operating !== null ? { level: 'ok', code: 'bank_operating' } : { level: 'missing', code: 'bank_operating_missing' });
    checks.push(p.bank.reserve !== null || p.bank.investments !== null ? { level: 'ok', code: 'bank_reserve' } : { level: 'warning', code: 'bank_reserve_missing' });
    const total = p.budget.filter((b) => b.accountNumber.startsWith('5') || b.accountNumber.startsWith('6')).reduce((s, b) => s + b.amountCents, 0);
    checks.push(p.budget.length ? { level: 'ok', code: 'budget', count: Math.round(total / 100) } : { level: 'warning', code: 'budget_missing' });
    if (p.arrears.length) {
      checks.push({ level: 'ok', code: 'arrears', count: p.arrears.length });
      const known = new Set(ctx.lots.map((l) => l.toLowerCase()));
      const unknown = p.arrears.filter((a) => !known.has(a.lot.toLowerCase())).map((a) => a.lot);
      if (unknown.length && known.size) checks.push({ level: 'warning', code: 'arrears_unknown_lots', count: unknown.length, items: examples(unknown) });
    } else checks.push({ level: 'warning', code: 'arrears_none' });
    if (!p.balancesDate) checks.push({ level: 'warning', code: 'balances_date_missing' });
    return checks;
  }
  const p = proposal as VendorsProposal;
  const checks: Check[] = [p.vendors.length ? { level: 'ok', code: 'vendors', count: p.vendors.length } : { level: 'warning', code: 'vendors_missing' }];
  const noEmail = p.vendors.filter((v) => !v.email).map((v) => v.name);
  if (noEmail.length) checks.push({ level: 'warning', code: 'vendors_no_email', count: noEmail.length, items: examples(noEmail) });
  return checks;
}

/** A step can be applied when nothing blocking is missing. */
export const canApply = (checks: Check[]) =>
  !checks.some((c) => c.level === 'missing' && ['lots_missing', 'address_missing', 'lots_without_owner', 'fiscal_year_missing', 'directors_missing', 'directors_no_email'].includes(c.code));

/** "3,125 %", "31/1000", "0.03125", "31,25" → percent. */
export function parseShare(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return value > 0 && value <= 1 ? value * 100 : value > 0 && value <= 100 ? value : null;
  const s = value.replace(/\s/g, '').replace(',', '.');
  const frac = /^(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)$/.exec(s);
  if (frac) return (Number(frac[1]) / Number(frac[2])) * 100;
  const n = Number(s.replace('%', ''));
  if (!Number.isFinite(n) || n <= 0) return null;
  if (s.includes('%')) return n <= 100 ? n : null;
  return n <= 1 ? n * 100 : n <= 100 ? n : null;
}

/** "Français", "F", "fr", "anglais", "English", "EN" → fr | en. */
export function parseLanguage(value: string | null | undefined): 'fr' | 'en' | null {
  const v = (value ?? '').trim().toLowerCase();
  if (!v) return null;
  if (/^(f|fr|fra|fran[cç]ais|french)/.test(v)) return 'fr';
  if (/^(a|an|ang|anglais|e|en|eng|english)/.test(v)) return 'en';
  return null;
}
